<script lang="ts">
  import type { Plan } from '$lib/types'
  import type { FootprintStats, ProductionTotals } from '$lib/stats'
  import { currentTheme, cycleTheme, themeMeta } from '$lib/theme'
  import ToolButton from './ToolButton.svelte'

  const activeTheme = $derived(themeMeta($currentTheme))

  interface Props {
    plan: Plan
    /** Footprint stats. Phase 2.5 — expansions / free squares / used % /
     *  building count. CityEditor + CityView both compute via $derived. */
    stats?: FootprintStats | null
    /** Production totals — Phase 4. CityEditor passes the validation-aware
     *  result; CityView passes a permissive total (no invalidBuildingIds). */
    production?: ProductionTotals | null
    onBack: () => void
    onRename: (name: string) => void
    onResetToImport: () => void
    onExport: () => void
    onZoomIn: () => void
    onZoomOut: () => void
    onZoomFit: () => void
    onZoomReset: () => void
    /** Editor-only: undo/redo controls. Omitted in the read-only view. */
    onUndo?: () => void
    onRedo?: () => void
    canUndo?: boolean
    canRedo?: boolean
  }

  // `production` stays on the Props interface so callers don't need to
  // change while the production tiles are suspended (see template comment
  // for the reason); intentionally not destructured here because nothing
  // in the template reads it.
  const {
    plan,
    stats = null,
    onBack,
    onRename,
    onResetToImport,
    onExport,
    onZoomIn,
    onZoomOut,
    onZoomFit,
    onZoomReset,
    onUndo,
    onRedo,
    canUndo = false,
    canRedo = false,
  }: Props = $props()

  /** Format integers with thousands separators so a 449-building plan reads
   *  "449" but a 12k-tile city reads "12,345". Per-day production numbers
   *  for late-game cities can run into millions, so the separator is
   *  load-bearing for readability. */
  const fmtN = (n: number): string => Math.round(n).toLocaleString('en-US')

  let editing = $state(false)
  let draftName = $state('')

  $effect(() => {
    if (!editing) draftName = plan.name
  })

  function startEdit() {
    draftName = plan.name
    editing = true
  }
  function commit() {
    editing = false
    const trimmed = draftName.trim()
    if (trimmed && trimmed !== plan.name) onRename(trimmed)
  }
  function cancel() {
    editing = false
    draftName = plan.name
  }
  function onNameKey(ev: KeyboardEvent) {
    if (ev.key === 'Enter') commit()
    else if (ev.key === 'Escape') cancel()
  }

  function confirmReset() {
    if (confirm('Discard your current layout and restore the city as it was loaded?')) {
      onResetToImport()
    }
  }
</script>

<header class="flex items-center gap-3 border-b border-border-rule bg-surface-5 px-4 py-2 text-chrome-text">
  <button
    type="button"
    class="rounded px-2 py-1 text-sm text-chrome-text hover:bg-surface-3"
    onclick={onBack}
    aria-label="Back to city list">← Back</button
  >

  <div class="min-w-0 shrink-0">
    {#if editing}
      <input
        class="w-64 rounded border border-accent bg-input-bg px-2 py-1 text-base font-semibold text-input-text focus:outline-none"
        bind:value={draftName}
        onblur={commit}
        onkeydown={onNameKey}
      />
    {:else}
      <button
        type="button"
        class="truncate rounded px-2 py-1 text-base font-semibold hover:bg-surface-3"
        onclick={startEdit}
        title="Click to rename"
      >
        {plan.name}
      </button>
    {/if}
  </div>

  {#if stats}
    <!-- Inline footprint summary. Pre-Phase-5 location for these numbers;
         restored after the dedicated stats bar emptied out (road-efficiency
         dropped, production tiles suspended). Same calculations as the
         old stat tiles / different visual treatment — single-line text on
         the same baseline as the city name, with parentheticals in the
         muted chrome tone for the two stats that have a secondary number.
         text-center centres the stats inside the flex-1 slot so they sit
         in the middle of the bar regardless of how much chrome flanks
         them. Production tiles will land here when bonus integration
         ships; see Toolbar.svelte history. -->
    <div
      class="min-w-0 flex-1 truncate text-center text-sm text-chrome-text"
      aria-label="City stats"
    >
      {fmtN(stats.expansions)} expansions
      <span class="text-chrome-text-2">({fmtN(stats.totalUnlocked)} tiles)</span>
      <span class="px-1 text-chrome-text-2" aria-hidden="true">·</span>
      {fmtN(stats.free)} free
      <span class="text-chrome-text-2">({fmtN(stats.occupied)} used)</span>
      <span class="px-1 text-chrome-text-2" aria-hidden="true">·</span>
      {stats.usedPercent}% used
      <span class="px-1 text-chrome-text-2" aria-hidden="true">·</span>
      {fmtN(stats.buildingCount)} buildings
      <span class="px-1 text-chrome-text-2" aria-hidden="true">·</span>
      {fmtN(stats.roadCount)} roads
    </div>
  {:else}
    <div class="flex-1"></div>
  {/if}

  {#if onUndo || onRedo}
    <div class="flex items-center gap-1">
      <button
        type="button"
        class="rounded px-2 py-1 text-xs text-chrome-text-2 hover:bg-surface-3 disabled:opacity-40"
        disabled={!canUndo}
        onclick={() => onUndo?.()}
        title="Undo (Ctrl+Z)"
        aria-label="Undo">↶</button
      >
      <button
        type="button"
        class="rounded px-2 py-1 text-xs text-chrome-text-2 hover:bg-surface-3 disabled:opacity-40"
        disabled={!canRedo}
        onclick={() => onRedo?.()}
        title="Redo (Ctrl+Shift+Z)"
        aria-label="Redo">↷</button
      >
    </div>
  {/if}

  <div class="flex items-center gap-1">
    <button
      type="button"
      class="rounded px-2 py-1 text-xs text-chrome-text-2 hover:bg-surface-3"
      onclick={onZoomOut}
      aria-label="Zoom out">−</button
    >
    <button
      type="button"
      class="rounded px-2 py-1 text-xs text-chrome-text-2 hover:bg-surface-3"
      onclick={onZoomReset}
      aria-label="Zoom 100%">100%</button
    >
    <button
      type="button"
      class="rounded px-2 py-1 text-xs text-chrome-text-2 hover:bg-surface-3"
      onclick={onZoomIn}
      aria-label="Zoom in">+</button
    >
    <button
      type="button"
      class="rounded px-2 py-1 text-xs text-chrome-text-2 hover:bg-surface-3"
      onclick={onZoomFit}
      aria-label="Fit to screen">Fit</button
    >
  </div>

  <ToolButton
    ico={activeTheme.ico}
    label={activeTheme.label}
    title={`Theme: ${activeTheme.label} — click to cycle`}
    onclick={() => cycleTheme()}
  />

  <button
    type="button"
    class="rounded bg-surface-3 px-3 py-1 text-xs text-chrome-text-2 hover:bg-surface-2"
    onclick={onExport}>Export city</button
  >
  <button
    type="button"
    class="rounded border border-status-neg/60 bg-status-neg/30 px-3 py-1 text-xs font-medium text-white shadow-sm hover:bg-status-neg/55 hover:border-status-neg"
    onclick={confirmReset}>Reset city</button
  >
</header>
